/**
 * Shared Data Cache
 * 
 * @copyright NIIT Technologies Ltd. 2017-18
 */

import { User } from './base.data';
import { FunctionAccessControl } from './access.data';

/**
 * Shared Data Cache
 */
export class SharedDataCache {
    private static _user: User = null
    private static _sharedResponse: any = null;
    private static _sharedData: any = null;
    private static _accessControlList: Object = {};
    private static _accessControlData: Object = {};
    private static _i18nData: Object = {};

    /**
     * Initialize
     */
    constructor() {
    }

    /**
     * Gets Shared User Information Data
     * 
     * @return User Information
     */
    public static getUserInfo(): User {
        return SharedDataCache._user;
    }

    /**
     * Sets Shared User Information to Global Shared User Information
     * 
     * @param user The User Information to be Shared
     */
    public static updateUserInfo(user: User) {
        SharedDataCache._user = user;
    }

    /**
     * Gets Shared Response Data
     * 
     * @return Shared Response
     */
    public static getSharedResponse(): any {
        return SharedDataCache._sharedResponse;
    }

    /**
     * Sets Shared Response Data to Global Shared Response
     * 
     * @param response The Response Data to be Shared
     */
    public static updateSharedResponse(response: any) {
        SharedDataCache._sharedResponse = response;
    }

    /**
     * Gets Shared Data
     * 
     * @return Shared Data
     */
    public static getSharedData(): any {
        return SharedDataCache._sharedData;
    }

    /**
     * Sets Shared Data to Global Shared Data
     * 
     * @param data The Data to be Shared
     */
    public static updateSharedData(data: any) {
        SharedDataCache._sharedData = data;
    }

    /**
     * Gets Shared Function Access Control Data
     * 
     * @param functionId Function Id
     * 
     * @return Function Access Control Data
     */
    public static getFunctionAccessData(functionId: string): FunctionAccessControl {
        if (!functionId) {
            return null;
        }
        return SharedDataCache._accessControlData[functionId];
    }

    /**
     * Remove Shared Function Access Control Data
     * 
     * @param functionId Function Id
     */
    public static removeFunctionAccessData(functionId: string): FunctionAccessControl {
        if (!functionId) {
            return;
        }
        SharedDataCache._accessControlData[functionId] = null;
    }

    /**
     * Updates Function Access Control Data to Global Shared Data
     * 
     * @param data Function Access Control Data
     */
    public static updateFunctionAccessData(data: FunctionAccessControl): void {
        if (!data || !data.getFunctionAccess() || !data.getFunctionAccess().id) {
            return;
        }
        SharedDataCache._accessControlData[data.getFunctionAccess().id] = data;
    }

    /**
     * Gets i12n Data
     * 
     * @param name i18n Name/Message
     * @param group i18n Group Id
     * 
     * @return l12n Name/Message
     */
    public static getI18NData(name: string, group: string): string {
        if (!name || !group) {
            return null;
        }
        return (SharedDataCache._i18nData[group]) ? SharedDataCache._i18nData[group][name] : null;
    }

    /**
     * Remove i18n Group Data
     *
     * @param group i18n Group Id
     */
    public static removeI18NGroup(group: string): void {
        if (!group) {
            return;
        }
        SharedDataCache._i18nData[group] = null;
    }

    /**
     * Sets i18n Data to Global Shared Data
     * 
     * @param name i18n Name/Message
     * @param group i18n Group Id
     * @param data l12n Name/Message
     */
    public static updateI18NData(name: string, group: string, data: string): void {
        if (!name || !group) {
            return;
        }
        SharedDataCache._i18nData[group][name] = data;
    }
}